import React, { memo, useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import studyService from '../../services/studyService';
import siteService from '../../services/siteService';
import { AuthContext } from '../../contexts/AuthContext';
import Sites from '../Site/Sites';
import StudyMFASettings from './StudyMFASettings';
import StudyDeviceConfig from './StudyDeviceConfig';
import DCQs from '../DCQ/DCQs';
import StudyCaregiversTab from '../Caregivers/StudyCaregiversTab';
import { Activity, Shield, FileText, Briefcase, ChevronLeft, Edit, Loader, BarChart, Upload, UserPlus, Users, UserCog, Bell, Book, Smartphone, Package } from 'lucide-react';
import PageHeader from '../Shared/PageHeader';
import ContextualNotificationToggle from '../Notifications/ContextualNotificationToggle';
import { Tab } from '@headlessui/react';
import AddUserModal from '../User/AddUserModal';
import BulkCreateParticipantsModal from '../Participant/BulkCreateParticipantsModal';
import { useLocation } from 'react-router-dom';
import hasUserRole from '../../utils/UserUtil'
import studyUtil from '../../utils/StudyUtil'
import permissionsService from '../../services/permissionsService'
import { PROMOTE_STUDY_BUILD_PERMISSION } from '../../services/studyPromotionApi'
import ExportCandidateModal from '../StudyPromotion/ExportCandidateModal'

const formatDate = (dateString) => {
  if (!dateString) return 'TBD';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
  const year = date.getFullYear();
  return `${day}${month}${year}`;
};

const getTabsForUser = (user, canManageSecurity = false, study = null) => {
  // TODO: Eventually consolidate device config, portal settings, and other study
  // settings into a unified "Configuration" tab or a dedicated study settings page.
  // For now, device config lives as its own tab. See also EditStudy.jsx.
  const isInternal = user?.is_internal || user?.is_superuser;

  const baseTabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'sites', label: 'Sites', icon: Briefcase },
    { id: 'dcqs', label: 'DCQs', icon: FileText },
  ];

  // Devices tab — internal users only (site staff don't manage device pools).
  if (isInternal) {
    baseTabs.push({ id: 'devices', label: 'Devices', icon: Smartphone });
  }

  // Caregivers tab — superuser/study staff only, gated on study toggle (FECOA-1320).
  if (study?.allow_caregivers && (user?.is_superuser || canManageSecurity)) {
    baseTabs.push({ id: 'caregivers', label: 'Caregivers', icon: UserCog });
  }

  // Only show Security tab to internal users with manage_study_security permission
  if (isInternal && (canManageSecurity || user?.is_superuser)) {
    baseTabs.push({ id: 'security', label: 'Security', icon: Shield });
  }

  // Add Notifications tab for all authenticated users
  baseTabs.push({ id: 'notifications', label: 'Notifications', icon: Bell });

  return baseTabs;
};

const SingleStudy = memo(() => {
  const { studyID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useContext(AuthContext);

  const [study, setStudy] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isBulkCreateOpen, setIsBulkCreateOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [hasOnlyOneStudy, setHasOnlyOneStudy] = useState(null); // null = checking, false = multiple studies, true = one study
  const [canManageSecurity, setCanManageSecurity] = useState(false);
  const [permissions, setPermissions] = useState({
    canChangeStudy: false,
    canAddUser: false,
    canAddSite: false,
    canBulkCreateParticipants: false,
  });

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (user) {
        try {
          const [hasSecurityPermission, hasChangeStudy, hasAddUser, hasAddSite, hasBulkCreate, hasPromote] = await Promise.all([
            permissionsService.hasPermission(permissionsService.PERMISSIONS.MANAGE_STUDY_SECURITY),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.CHANGE_STUDY),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ADD_USER),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ADD_SITE),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.BULK_CREATE_PARTICIPANTS),
            permissionsService.hasPermission(PROMOTE_STUDY_BUILD_PERMISSION),
          ]);

          setCanManageSecurity(hasSecurityPermission);
          setPermissions({
            canChangeStudy: hasChangeStudy,
            canAddUser: hasAddUser,
            canAddSite: hasAddSite,
            canBulkCreateParticipants: hasBulkCreate,
            canPromoteStudyBuild: hasPromote,
          });

          // Debug logging
          console.log('[SINGLE_STUDY] Permissions set:', {
            canChangeStudy: hasChangeStudy,
            canAddUser: hasAddUser,
            canAddSite: hasAddSite,
            canBulkCreateParticipants: hasBulkCreate,
          });
        } catch (error) {
          console.error('Error checking permissions:', error);
          setCanManageSecurity(false);
          setPermissions({
            canChangeStudy: false,
            canAddUser: false,
            canAddSite: false,
            canBulkCreateParticipants: false,
          });
        }
      }
    };
    
    checkPermissions();
  }, [user]);

  // Get tabs based on user permissions
  const tabs = getTabsForUser(user, canManageSecurity, study);

  const getActiveTabFromURL = () => {
    // Check query parameter first (?tab=notifications)
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const t = tabs.findIndex(tab => tab.id === tabParam);
      if (t >= 0) return t;
    }
    
    // Fall back to hash (#notifications)
    if (location.hash) {
      const id = location.hash.substring(1);
      const t = tabs.findIndex(tab => tab.id === id);
      if (t >= 0) return t;
    }
    
    return 0;
  }

  const [activeTab, setActiveTab] = useState(getActiveTabFromURL());

  // Update active tab when URL changes (e.g., clicking "View" from notifications page)
  useEffect(() => {
    const newTab = getActiveTabFromURL();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.search, location.hash]);

  const updateActiveTab = (tab) => {
    const tabId = tabs[tab].id;
    if (tabId == 'overview') 
      navigate('', { replace: true });
    else
      navigate(`#${tabs[tab].id}`, { replace: true });
    setActiveTab(tab)
  }

    useEffect(() => {
      const loadStudyData = async () => {
        setLoading(true);
        try {
          const studyData = await studyService.getSingleStudy(studyID);
          const sitesData = await siteService.getSitesForStudy(studyID);
          
          if (studyData) {
            console.log('Study Data:', studyData); // Debug log
            setStudy(studyData);
            setSites(sitesData);
          } else {
            setError('No study data found.');
          }
        } catch (error) {
          console.error('Error fetching study:', error);
          setError('An error occurred while fetching the study.');
        } finally {
          setLoading(false);
        }
      };

      if (isAuthenticated) {
        loadStudyData();
      }
    }, [studyID, isAuthenticated]);

  // Check if user has only one study
  useEffect(() => {
    const checkStudyCount = async () => {
      if (user) {
        try {
          const onlyOneStudy = await studyUtil.hasOnlyOneStudy();
          setHasOnlyOneStudy(onlyOneStudy);
        } catch (error) {
          console.error('Error checking study count:', error);
        }
      }
    };

    checkStudyCount();
  }, [user]);

  // Set active tab from URL once user is loaded
  useEffect(() => {
    if (user) {
      setActiveTab(getActiveTabFromURL());
    }
  }, [user]);

  // Some tabs only appear once permissions and the study have loaded, which
  // shifts their positions, so match the tab in the address again.
  const tabIdsKey = tabs.map((tab) => tab.id).join(',');
  useEffect(() => {
    setActiveTab(getActiveTabFromURL());
  }, [tabIdsKey]);

  const handleAddUserSuccess = () => {
    setIsAddUserModalOpen(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <Loader className="w-16 h-16 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl dark:shadow-gray-900/20">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-700 dark:text-gray-200">{error}</p>
        </div>
      </div>
    );
  }

  const totalParticipants = sites.reduce((sum, site) => sum + (site.total_participants_count || 0), 0);
  const participantsInUse = sites.reduce((sum, site) => sum + (site.active_participants_count || 0), 0);
  const participantsInUsePercentage = totalParticipants > 0 ? (participantsInUse / totalParticipants) * 100 : 0;

  
  
  const studyDetails = {
    totalParticipants,
    participantsInUse
  };

  return (
    <div>
      <div>
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          {hasOnlyOneStudy === false && (
            <Link
              to="/studies"
              className="back-link"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Studies
            </Link>
          )}
          <PageHeader
            icon={Book}
            title={study?.title ? (/\bstudy$/i.test(study.title.trim()) ? study.title : `${study.title} Study`) : 'Study'}
            subtitle="Manage study details, sites, and data queries"
          >
            <Link
              to={`/studies/${studyID}/compliance`}
              className="btn btn-primary"
            >
              <BarChart className="h-4 w-4" />
              Compliance & Reporting
            </Link>

            {permissions.canChangeStudy && (
              <Link
                to={`/studies/${studyID}/edit`}
                className="btn btn-secondary"
              >
                <Edit className="h-4 w-4" />
                Edit Study
              </Link>
            )}

            {permissions.canAddSite && (
              <button
                onClick={() => navigate(`/studies/${studyID}/upload`)}
                className="btn btn-secondary"
              >
                <Upload className="h-4 w-4" />
                Upload Sites & Users
              </button>
            )}

            {permissions.canAddUser && (
              <button
                onClick={() => setIsAddUserModalOpen(true)}
                className="btn btn-secondary"
              >
                <UserPlus className="h-4 w-4" />
                Add User
              </button>
            )}

            {permissions.canBulkCreateParticipants && (
              <button
                onClick={() => setIsBulkCreateOpen(true)}
                className="btn btn-secondary"
              >
                <Users className="h-4 w-4" />
                Bulk Add Participants
              </button>
            )}

            {permissions.canPromoteStudyBuild && (
              <button
                onClick={() => setIsExportOpen(true)}
                className="btn btn-secondary"
              >
                <Package className="h-4 w-4" />
                Export Candidate
              </button>
            )}
          </PageHeader>
          {error ? (
            <div className="text-red-600 mb-4">{error}</div>
          ) : (
            <>
              <AddUserModal
                isOpen={isAddUserModalOpen}
                onClose={() => setIsAddUserModalOpen(false)}
                onSuccess={handleAddUserSuccess}
                sponsorId={study?.sponsor?.id}
                studyId={studyID}
              />
              <BulkCreateParticipantsModal
                isOpen={isBulkCreateOpen}
                onClose={() => setIsBulkCreateOpen(false)}
                onSuccess={() => { setIsBulkCreateOpen(false); window.location.reload(); }}
                studyId={studyID}
                sites={sites}
                studyName={study?.title || ''}
                sponsorCode={study?.sponsor?.code || study?.sponsor?.name || ''}
              />
              <ExportCandidateModal
                isOpen={isExportOpen}
                onClose={() => setIsExportOpen(false)}
                studyId={studyID}
                studyCode={study?.code || ''}
                studyTitle={study?.title || ''}
              />
            </>
          )}
          <div className="card overflow-hidden">
            <div className="card-band">
              <h2 className="card-band-title">Study Details</h2>
            </div>
            <div className="p-6">
              <Tab.Group selectedIndex={activeTab} onChange={updateActiveTab}>
                <Tab.List className="tabs-list mb-6">
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      className={({ selected }) => `tab ${selected ? 'tab-active' : ''}`}
                    >
                      <tab.icon size={16} />
                      {tab.label}
                    </Tab>
                  ))}
                </Tab.List>
                <Tab.Panels>
                  <Tab.Panel>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                          <div className="stat-label">
                            <Users className="h-3.5 w-3.5 text-primary-500" />
                            Total Participants
                          </div>
                          <p className="stat-value">{studyDetails.totalParticipants}</p>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                          <div className="flex items-baseline justify-between gap-3">
                            <div className="stat-label">
                              <Activity className="h-3.5 w-3.5 text-primary-500" />
                              Participants In Use
                            </div>
                            <span className="text-xs font-semibold tabular-nums text-gray-500 dark:text-gray-400">
                              {Math.round(participantsInUsePercentage)}%
                            </span>
                          </div>
                          <p className="stat-value">
                            {studyDetails.participantsInUse}
                            <span className="ml-1 text-base font-semibold text-gray-400 dark:text-gray-500">/ {studyDetails.totalParticipants}</span>
                          </p>
                          <div
                            className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(participantsInUsePercentage)}
                            aria-label="Participants in use"
                          >
                            <div
                              style={{ width: `${participantsInUsePercentage}%` }}
                              className="h-full rounded-full bg-primary-600 transition-all duration-1000 ease-out dark:bg-primary-500"
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </Tab.Panel>
                  <Tab.Panel>
                    <Sites studyID={studyID} />
                  </Tab.Panel>
                  <Tab.Panel>
                    <DCQs studyID={studyID} />
                  </Tab.Panel>
                  {tabs.some((t) => t.id === 'devices') && (
                    <Tab.Panel>
                      <StudyDeviceConfig studyID={studyID} canEdit={permissions.canChangeStudy} />
                    </Tab.Panel>
                  )}
                  {/* Conditional panels keep Tab.Panel indices aligned with the
                      dynamic tabs array (caregivers/security may be absent). */}
                  {tabs.some((t) => t.id === 'caregivers') && (
                    <Tab.Panel>
                      <StudyCaregiversTab studyID={studyID} />
                    </Tab.Panel>
                  )}
                  {tabs.some((t) => t.id === 'security') && (
                    <Tab.Panel>
                      <StudyMFASettings studyId={studyID} />
                    </Tab.Panel>
                  )}
                  <Tab.Panel>
                    {/* Notifications Tab */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="max-w-4xl mx-auto">
                        <div className="card overflow-hidden">
                          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-800">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="rounded-lg bg-primary-50 p-2 dark:bg-primary-900/20">
                                  <Bell className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Study Notifications</h3>
                                  <p className="text-sm text-gray-600 dark:text-gray-300">Manage notification preferences for this study</p>
                                </div>
                              </div>
                              <Link
                                to="/profile?tab=notifications"
                                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors flex items-center gap-1"
                              >
                                <span>Advanced settings</span>
                                <span>→</span>
                              </Link>
                            </div>
                          </div>
                          
                          <div className="p-6">
                            <div className="space-y-4">
                              <ContextualNotificationToggle
                                eventKey="survey.missed"
                                label="Missed Surveys"
                                description="Get notified when participants miss their survey deadlines"
                                studyId={studyID}
                              />
                              
                              <ContextualNotificationToggle
                                eventKey="participant.created"
                                label="New Participants"
                                description="Know when new participants are enrolled in this study"
                                studyId={studyID}
                              />
                              
                              <ContextualNotificationToggle
                                eventKey="dcq.created"
                                label="New Data Queries"
                                description="Get notified when new data clarification queries are created"
                                studyId={studyID}
                              />

                              <ContextualNotificationToggle
                                eventKey="dcq.updated"
                                label="Data Query Updates"
                                description="Be alerted when queries are updated, commented on, or status changes"
                                studyId={studyID}
                              />

                              <ContextualNotificationToggle
                                eventKey="participant.activated"
                                label="Participant Activations"
                                description="Be informed when participants are activated"
                                studyId={studyID}
                              />
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-start gap-2">
                              <span className="text-lg">💡</span>
                              <div className="flex-1">
                                <p className="text-sm text-gray-700 dark:text-gray-200 font-medium mb-1">About Study Notifications</p>
                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                  These notifications are specific to <strong>{study?.title || 'this study'}</strong>. 
                                  You'll only receive alerts for events that happen within this study's sites and participants.
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-300 mt-2">
                                  For global notification preferences or to configure notifications across all studies, 
                                  visit your <Link to="/profile?tab=notifications" className="text-primary-600 hover:underline font-medium">notification settings</Link>.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
});

export default SingleStudy;